/**
 * dataEntryModel — pure helpers of «הזנה חכמה» (ADR-0037, Turbo's dataEntryModel; no React).
 * A row = the server proposal + local edits + approval / commit state. The rows live here, on the
 * client; the server gets the full values of each approved row (commitPayload).
 */
import type {
  CommitResultDto,
  CommitRowBody,
  CounterpartyType,
  Currency,
  DataEntryRowDto,
  EntityAction,
  EntityEntryDto,
  ImportType,
  LearnChange,
  PaymentMethod,
  RowKind,
  RowsStateItem,
} from "../../../api/aiDataEntryApi";

export type Tone = "neutral" | "primary" | "success" | "warning" | "danger" | "info" | "violet";

export const KIND_META: Record<RowKind, { label: string; icon: string; tone: Tone; dir: "in" | "out" | null; key: string }> = {
  payment: { label: "תשלום מלקוח", icon: "users", tone: "primary", dir: "in", key: "1" },
  income: { label: "הכנסה", icon: "wallet", tone: "success", dir: "in", key: "2" },
  expense: { label: "הוצאה", icon: "receipt", tone: "danger", dir: "out", key: "3" },
  sale: { label: "מכירה חדשה", icon: "box", tone: "violet", dir: "in", key: "4" },
  skip: { label: "דלג", icon: "eyeOff", tone: "neutral", dir: null, key: "0" },
};
export const KIND_ORDER: RowKind[] = ["payment", "income", "expense", "sale", "skip"];

export const IMPORT_TYPE_OPTIONS: Array<{ value: ImportType; label: string; icon: string; description: string }> = [
  { value: "auto", label: "זיהוי אוטומטי", icon: "sparkle", description: "הסוכן מזהה לכל שורה אם היא תשלום מלקוח, הכנסה, הוצאה או מכירה" },
  { value: "bank", label: "דף בנק", icon: "bank", description: "תנועות נכנסות ויוצאות — שיוך ללקוחות ולספקים" },
  { value: "payments", label: "תשלומים מלקוחות", icon: "users", description: "כל השורות הן תשלומים — שיוך ללקוח, ולעסקה פתוחה אם יש" },
  { value: "incomes", label: "הכנסות", icon: "wallet", description: "הכנסות לקופה שאינן תשלום של לקוח מסוים" },
  { value: "expenses", label: "הוצאות", icon: "receipt", description: "תשלומים לספקים — שיוך לספק של הקופה" },
  { value: "sales", label: "מכירות חדשות", icon: "box", description: "כל שורה היא עסקה חדשה — לקוח + מוצר / קורס" },
  { value: "customers", label: "רשימת לקוחות", icon: "user", description: "לא תנועות — רשימה של לקוחות: חדשים נוצרים, קיימים מושלמים / מתעדכנים, בלי כפילויות" },
  { value: "suppliers", label: "רשימת ספקים", icon: "building", description: "רשימה של ספקים לקופה שנבחרה — ספק שכבר קיים לא נוצר שוב" },
];

/** a list of records (the entity sub-flow, DataEntryEntity) — not money movements */
export const isEntityImport = (t: ImportType | null | undefined): t is "customers" | "suppliers" => t === "customers" || t === "suppliers";

export const PAYMENT_OPTIONS: Array<{ value: PaymentMethod; label: string }> = [
  { value: "bank_transfer", label: "העברה בנקאית" },
  { value: "cash", label: "מזומן" },
  { value: "check", label: "צ'ק" },
  { value: "credit_card", label: "כרטיס אשראי" },
  { value: "app", label: "אפליקציה (ביט / פייבוקס)" },
  { value: "standing_order", label: "הוראת קבע" },
  { value: "other", label: "אחר" },
];

export const SOURCE_LABELS: Record<string, string> = {
  file: "מהקובץ",
  history: "כמו בפעם הקודמת",
  ai: "הצעת AI",
  default: "ברירת מחדל",
  file_default: "נקבע לכל הקובץ",
  guess: "זוהה מהתיאור",
  rule: "לפי כלל",
  chat: "לפי השיחה",
  manual: "נבחר ידנית",
};

export const MATCH_LABELS: Record<string, string> = {
  phone: "לפי טלפון",
  id_number: "לפי ת.ז / ח.פ",
  email: "לפי מייל",
  code: "לפי קוד לקוח",
  name: "לפי שם",
  manual: "נבחר ידנית",
  created: "נוצר עכשיו",
  chat: "לפי השיחה",
};

const CURRENCY_SIGN: Record<string, string> = { ILS: "₪", USD: "$", EUR: "€", GBP: "£" };
export const CURRENCY_OPTIONS: Array<{ value: Currency; label: string }> = [
  { value: "ILS", label: "₪ שקל" },
  { value: "USD", label: "$ דולר" },
  { value: "EUR", label: "€ יורו" },
  { value: "GBP", label: "£ לירה" },
];
export const money = (n: number | null | undefined, currency: string = "ILS") =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? "—"
    : `${CURRENCY_SIGN[currency] ?? `${currency} `}${Number(n).toLocaleString("he-IL", { maximumFractionDigits: 2 })}`;
const round2 = (n: number) => Math.round(n * 100) / 100;
export const agorot = (n: number | null | undefined) => (n === null || n === undefined ? "—" : money(n / 100));
export const shortDate = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

export function wantedCounterpartyType(kind: RowKind): CounterpartyType | null {
  if (kind === "skip") return null;
  return kind === "expense" ? "supplier" : "customer";
}

export type CommitState =
  | { state: "idle" }
  | { state: "queued" }
  | { state: "saving" }
  | { state: "done"; result: CommitResultDto & { local?: boolean } }
  | { state: "error"; error: string };

export type CategorySplit = { categoryId: string | null; amount: number };

export type LocalRow = DataEntryRowDto & {
  approved: boolean;
  commit: CommitState;
  touched: Partial<Record<string, boolean>>;
  /** «פיצול לקטגוריות» (income / expense) — null = one category */
  categorySplits: CategorySplit[] | null;
};

export function toLocalRow(r: DataEntryRowDto): LocalRow {
  return {
    ...r,
    currency: r.currency ?? "ILS",
    exchangeRate: r.exchangeRate ?? null,
    bankLine: r.bankLine ?? null,
    useBank: Boolean(r.bankLine && r.useBank !== false),
    existingAction: r.existing ? (r.existingAction ?? "keep") : null,
    approved: false,
    commit: { state: "idle" },
    touched: {},
    categorySplits: null,
  };
}

/** a split is offered where the record has a category of its own (incomes / expenses in a ledger) */
export const canSplit = (row: Pick<LocalRow, "kind">) => row.kind === "income" || row.kind === "expense";

/** «פיצול לשתי קטגוריות»: half each — the current category stays in the first part */
export function startSplit(row: Pick<LocalRow, "amount" | "category">): CategorySplit[] {
  const total = Number(row.amount) || 0;
  const half = round2(total / 2);
  return [
    { categoryId: row.category?.id ?? null, amount: half },
    { categoryId: null, amount: round2(total - half) },
  ];
}

/** one part's amount changed — with two parts the other one takes the rest (Turbo's split editor) */
export function setSplitAmount(splits: CategorySplit[], i: number, amount: number, total: number): CategorySplit[] {
  const next = splits.map((x, j) => (j === i ? { ...x, amount } : x));
  if (next.length === 2) next[1 - i] = { ...next[1 - i]!, amount: round2(total - amount) };
  return next;
}

export type Problem = { field: "amount" | "date" | "payment" | "counterparty" | "duplicate" | "ledger" | "split" | "currency"; text: string };

/** what still blocks approving this row (empty = ok) — the server runs the same rules */
export function rowProblems(row: DataEntryRowDto & { categorySplits?: CategorySplit[] | null }, opts: { hasLedger?: boolean } = {}): Problem[] {
  if (row.kind === "skip") return [];
  if (row.existing && row.existingAction !== "create") return [];
  if (row.duplicate && !row.duplicateAction) return [{ field: "duplicate", text: "דומה לרשומה קיימת — החלט אם לרשום" }];
  if (row.duplicateAction === "skip") return [];
  const out: Problem[] = [];
  if (!(Number(row.amount) > 0)) out.push({ field: "amount", text: "חסר סכום" });
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(row.date ?? ""))) out.push({ field: "date", text: "חסר תאריך" });
  if (!row.paymentMethod) out.push({ field: "payment", text: "בחר אמצעי תשלום" });
  if ((row.kind === "payment" || row.kind === "sale") && row.counterparty?.type !== "customer") {
    out.push({ field: "counterparty", text: row.kind === "sale" ? "מכירה חייבת שיוך ללקוח" : "תשלום חייב שיוך ללקוח" });
  }
  if (row.counterparty?.ambiguous) out.push({ field: "counterparty", text: "בחר את ההתאמה הנכונה (1–4)" });
  if ((row.kind === "income" || row.kind === "expense") && opts.hasLedger === false) out.push({ field: "ledger", text: "בחר קופה להכנסות והוצאות (בצד)" });
  const splits = row.categorySplits;
  if (splits?.length && canSplit(row)) {
    const sum = splits.reduce((a, x) => a + (Number(x.amount) || 0), 0);
    if (Math.abs(sum - Number(row.amount)) > 0.01) out.push({ field: "split", text: "סכומי הפיצול לא שווים לסכום השורה" });
    if (splits.some((x) => !x.categoryId)) out.push({ field: "split", text: "בחר קטגוריה לכל חלק בפיצול" });
    if (splits.some((x) => !(Number(x.amount) > 0))) out.push({ field: "split", text: "כל חלק בפיצול צריך סכום" });
  }
  // a payment on a deal is in shekels: a foreign amount needs its rate (the server refuses it otherwise)
  if (row.kind === "payment" && row.useDeal && row.deals[0] && (row.currency ?? "ILS") !== "ILS" && !row.exchangeRate) {
    out.push({ field: "currency", text: "תשלום על עסקה במטבע זר — צריך שער המרה" });
  }
  return out;
}

/** body sent to the server for one approved row */
export function commitPayload(row: LocalRow): CommitRowBody {
  const skipped = row.kind === "skip" || row.duplicateAction === "skip";
  const sourceNote = row.source && row.source !== row.description ? `מקור: ${row.source}` : null;
  return {
    index: row.index,
    kind: skipped ? "skip" : row.kind,
    date: row.date,
    amount: row.amount,
    paymentMethod: row.paymentMethod,
    counterparty: row.counterparty ? { type: row.counterparty.type, id: row.counterparty.id, label: row.counterparty.label } : null,
    newName: !row.counterparty ? (row.newEntity?.name ?? null) : null,
    categoryId: row.category?.id ?? null,
    fundId: row.kind === "income" || row.kind === "expense" ? (row.fund?.id ?? null) : null,
    description: row.description || null,
    notes: [row.notes, sourceNote].filter(Boolean).join(" · ") || null,
    reference: row.reference,
    repId: row.kind === "sale" ? (row.rep?.id ?? null) : null,
    productId: row.kind === "sale" ? (row.product?.id ?? null) : null,
    dealId: row.kind === "payment" && row.useDeal ? (row.deals[0]?.id ?? null) : null,
    salePaid: row.salePaid,
    duplicateAction: row.duplicateAction,
    existingAction: row.existing ? row.existingAction : null,
    currency: row.currency ?? "ILS",
    exchangeRate: (row.currency ?? "ILS") !== "ILS" ? (row.exchangeRate ?? null) : null,
    installments: row.installments && row.installments > 1 ? row.installments : null,
    categorySplits: row.categorySplits?.length && canSplit(row) ? row.categorySplits : undefined,
    bankLineId: row.useBank && row.bankLine && canSplit(row) ? row.bankLine.id : null,
  };
}

export type RowState = "ready" | "review" | "skip" | "skipped" | "saving" | "done" | "kept" | "error";

export function rowState(row: LocalRow): RowState {
  if (row.commit.state === "done") {
    const r = row.commit.result;
    if (r.skipped) return "skipped";
    if (r.kept) return "kept";
    return "done";
  }
  if (row.commit.state === "error") return "error";
  if (row.commit.state === "saving" || row.commit.state === "queued") return "saving";
  if (row.kind === "skip") return row.approved ? "skipped" : "skip";
  return row.status === "ready" ? "ready" : "review";
}

export const STATE_META: Record<RowState, { label: string; tone: Tone }> = {
  ready: { label: "מוכנה", tone: "success" },
  review: { label: "לבדיקה", tone: "warning" },
  skip: { label: "לדילוג", tone: "neutral" },
  skipped: { label: "דולגה", tone: "neutral" },
  saving: { label: "נרשמת…", tone: "info" },
  done: { label: "נרשמה", tone: "primary" },
  kept: { label: "ללא שינוי", tone: "neutral" },
  error: { label: "שגיאה", tone: "danger" },
};

export const isFinished = (row: LocalRow | undefined) => Boolean(row) && ["done", "skipped", "kept"].includes(rowState(row as LocalRow));

/** the single-row change that can be offered to similar rows ("learn from the correction") */
export function learnableChange(field: string, patch: Partial<LocalRow>): LearnChange | null {
  if (field === "kind" && patch.kind && patch.kind !== "skip") return { kind: patch.kind };
  if (field === "category" && patch.category?.id) return { categoryId: patch.category.id };
  if (field === "fund" && patch.fund?.id) return { fundId: patch.fund.id };
  return null;
}

export function describeChange(change: LearnChange, lookups: { categories: Array<{ id: string; name: string }>; funds: Array<{ id: string; name: string }> }) {
  if (change.kind) return KIND_META[change.kind].label;
  if (change.categoryId) return `קטגוריה ${lookups.categories.find((c) => c.id === change.categoryId)?.name ?? ""}`;
  if (change.fundId) return `קרן ${lookups.funds.find((f) => f.id === change.fundId)?.name ?? ""}`;
  return "";
}

/** filter value "flag:<code>" — rows the analysis flagged with that code */
export const matchesFlag = (row: LocalRow, filter: string) => filter.startsWith("flag:") && row.flags.includes(filter.slice(5));

export const normName = (s: string | null | undefined) =>
  String(s ?? "")
    .trim()
    .replace(/[״"'`׳]/g, "")
    .replace(/\s+/g, " ")
    .toLowerCase();

/** the rows as the agent should see them in the conversation (the user's edits included) */
export function rowsStateOf(rows: LocalRow[]): RowsStateItem[] {
  return rows.map((r) => ({
    i: r.index,
    kind: r.kind,
    cat: r.category?.id ?? null,
    catName: r.category?.name ?? null,
    cp: r.counterparty ? [r.counterparty.type, r.counterparty.id, r.counterparty.label] : null,
    pm: r.paymentMethod,
    desc: r.description || null,
    st: rowState(r),
    ea: r.existingAction,
    da: r.duplicateAction,
  }));
}

/** typing in an input should not trigger the deck's single-key shortcuts */
export function isTypingTarget(el: EventTarget | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

export const FLAG_LABELS: Record<string, string> = {
  existing_any: "כבר במערכת",
  new: "חדשה",
  new_entity: "חדש במערכת",
  name_only: "לפי שם בלבד",
  ambiguous: "כמה התאמות",
  partial_match: "התאמה חלקית",
  dup_possible: "דומה לקיימת",
  dup_in_file: "כפולה בקובץ",
  future_date: "תאריך עתידי",
  no_date: "בלי תאריך",
  recurring: "מחזורית",
  rule: "לפי כלל",
  open_deal: "עסקה פתוחה",
  no_ledger: "בלי קופה",
  negative: "סכום שלילי",
  no_payment_method: "בלי אמצעי תשלום",
  skipped: "ידולג",
  foreign_currency: "מטבע זר",
  installments: "בתשלומים",
  bank_line: "שורת בנק תואמת",
};

/* ── a list of customers / suppliers (DataEntryEntity) ── */

export type EntityCommit =
  | { state: "idle" }
  | { state: "saving" }
  | { state: "done"; action: "created" | "updated"; id: string; label: string }
  | { state: "passed"; note?: string }
  | { state: "failed"; error: string };

export type LocalEntity = EntityEntryDto & { commit: EntityCommit; edited: boolean };

export type EntityState = "ready" | "review" | "duplicate" | "error" | "saving" | "done" | "passed" | "failed";

export const ENTITY_STATE_META: Record<EntityState, { label: string; tone: Tone; icon: string }> = {
  ready: { label: "מוכן", tone: "primary", icon: "sparkle" },
  review: { label: "לבדיקה", tone: "warning", icon: "alert" },
  duplicate: { label: "כבר קיים", tone: "warning", icon: "link" },
  error: { label: "חסרים פרטים", tone: "danger", icon: "alert" },
  saving: { label: "שומר…", tone: "info", icon: "clock" },
  done: { label: "נרשם", tone: "success", icon: "check" },
  passed: { label: "דילוג", tone: "neutral", icon: "eyeOff" },
  failed: { label: "נכשל", tone: "danger", icon: "alert" },
};

export const ENTITY_ACTION_LABELS: Record<EntityAction, string> = {
  create: "ליצור חדש",
  skip: "לא לגעת",
  merge_empty_fields: "השלמת שדות ריקים",
  update_existing: "עדכון הרשומה",
};

export const entityState = (e: LocalEntity): EntityState => {
  if (e.commit.state === "done") return "done";
  if (e.commit.state === "saving") return "saving";
  if (e.commit.state === "passed") return "passed";
  if (e.commit.state === "failed") return "failed";
  return e.status;
};
export const entityIsTodo = (s: EntityState) => !["done", "passed", "saving"].includes(s);
export const entityNeedsAttention = (s: EntityState) => ["review", "duplicate", "error", "failed"].includes(s);
/** the entry cannot be written as it is (the user fixes the cells, or skips it) */
export const entityBlocked = (s: EntityState) => s === "error";

/** what approving this entry will do — skip when the user chose «לא לגעת» for a record that exists */
export function entityApproveKind(e: LocalEntity): "skip" | "write" {
  return e.duplicateAction === "skip" ? "skip" : "write";
}

export function toLocalEntities(entries: EntityEntryDto[], committed: Array<{ index: number; action: "created" | "updated"; id: string; label: string }>, prev: LocalEntity[] = []): LocalEntity[] {
  const done = new Map(committed.map((c) => [c.index, c]));
  const before = new Map(prev.map((e) => [e.index, e]));
  return entries.map((e) => {
    const old = before.get(e.index);
    const c = done.get(e.index);
    const action = old && e.actions.includes(old.duplicateAction) ? old.duplicateAction : e.duplicateAction;
    return {
      ...e,
      duplicateAction: action,
      edited: false,
      commit: c ? { state: "done", action: c.action, id: c.id, label: c.label } : old?.commit.state === "passed" ? old.commit : { state: "idle" },
    };
  });
}

export const entitySummary = (e: Pick<EntityEntryDto, "fields" | "raw">) =>
  e.fields
    .slice(0, 3)
    .map((f) => f.value)
    .join(" · ") ||
  e.raw
    .slice(0, 3)
    .map((c) => c.value)
    .join(" · ") ||
  "—";
